import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useInventoryMonthlyReports } from "@/hooks";
import { useMemo } from "react";

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
});

function formatCurrency(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "-";
  return currencyFormatter.format(value);
}

function formatNumber(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "-";
  return Math.round(value).toLocaleString();
}

function formatMonthLabel(monthKey: string): string {
  // monthKey is "YYYY-MM"
  const [year, month] = monthKey.split("-").map((part) => Number(part));
  if (!year || !month) return monthKey;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

export default function InventoryReportsPage() {
  const { data, isLoading } = useInventoryMonthlyReports();

  // Newest month first; sorted client-side so no extra Firestore index is needed.
  const reports = useMemo(
    () =>
      [...(data ?? [])].sort((left, right) =>
        right.monthKey.localeCompare(left.monthKey),
      ),
    [data],
  );

  return (
    <div className="container mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Inventory Throughput
        </h1>
        <p className="text-sm text-muted-foreground">
          Purchase and sales activity per month, with end-of-month miles on
          hand. For stock value and the supplier-payment breakdown over time,
          see the Inventory Value report.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Inventory by Month</CardTitle>
          <CardDescription>
            Per-month purchase and sales throughput. Voided purchase orders and
            cancelled sales orders are excluded.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table containerClassName="overflow-auto">
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Purchases</TableHead>
                <TableHead className="text-right">Sales</TableHead>
                <TableHead className="text-right">Miles Purchased</TableHead>
                <TableHead className="text-right">Miles Sold</TableHead>
                <TableHead className="text-right">Purchase Cost</TableHead>
                <TableHead className="text-right">Sales Revenue</TableHead>
                <TableHead className="text-right">Miles On Hand</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground"
                  >
                    Loading inventory reports...
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground"
                  >
                    No inventory reports yet. Run the backfill or wait for the
                    daily sync.
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow key={report.monthKey}>
                    <TableCell className="font-medium">
                      {formatMonthLabel(report.monthKey)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(report.purchaseCount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(report.salesCount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(report.milesPurchased)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(report.milesSold)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(report.purchaseCost)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(report.salesRevenue)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(report.milesOnHand)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
